/**
 * /honorarios/tarifario — Admin del tarifario P-01 (6 productos).
 * Rol: Dirección, Admin TI, Finanzas.
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PRODUCTOS, PRODUCTO_LABEL, fmtMXNDec, fmtDateCot } from "./_common";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Info } from "lucide-react";

function RowSummary({ tar }) {
    const p = tar.parametros || {};
    const a = p.administracion_anual || {};
    return (
        <tr className="border-t border-border/50 hover:bg-muted/30" data-testid={`tarifario-row-${tar.producto}`}>
            <td className="px-3 py-2 font-mono text-xs">{tar.producto}</td>
            <td className="px-3 py-2">{tar.nombre_comercial}</td>
            <td className="px-3 py-2 text-right text-xs">
                {fmtMXNDec(a.fijo_min_mxn)} – {fmtMXNDec(a.fijo_max_mxn)}
            </td>
            <td className="px-3 py-2 text-right text-xs">
                {a.pb_min}–{a.pb_max} bps
            </td>
            <td className="px-3 py-2 text-xs capitalize">{a.base_calculo?.replace("_", " ")}</td>
            <td className="px-3 py-2 text-center">
                <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200">v{tar.version}</Badge>
            </td>
            <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDateCot(tar.vigente_desde)}</td>
        </tr>
    );
}

export default function TarifarioPage() {
    const [tars, setTars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/honorarios/tarifario", { params: { vigente: true } })
            .then((r) => setTars(r.data || []))
            .catch((e) => setError(e?.response?.data?.detail || "Error cargando tarifarios"))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-6" data-testid="tarifario-page">
            <div>
                <h1 className="text-2xl font-serif font-semibold text-foreground" style={{ fontFamily: "'Fraunces', serif" }}>
                    Tarifario P-01 · Honorarios
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    6 productos del catálogo. Versión actual vigente. Las desviaciones por debajo del
                    mínimo requieren aprobación de Dirección General (G-01).
                </p>
            </div>

            <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                    Admin anual se calcula como <code>MAX(fijo, PB/10000 × base)</code>.
                    IVA 16% aplicable. Honorario anual pagadero por anualidad anticipada,
                    actualizable por INPC.
                </AlertDescription>
            </Alert>

            {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

            <Card>
                <CardHeader>
                    <CardTitle>Catálogo vigente</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase tracking-wide">
                            <tr>
                                <th className="px-3 py-2 text-left">Clave</th>
                                <th className="px-3 py-2 text-left">Producto</th>
                                <th className="px-3 py-2 text-right">Admin fijo (min–max)</th>
                                <th className="px-3 py-2 text-right">PB (min–max)</th>
                                <th className="px-3 py-2 text-left">Base PB</th>
                                <th className="px-3 py-2 text-center">Versión</th>
                                <th className="px-3 py-2 text-left">Vigente desde</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={7} className="px-3 py-4 text-center text-muted-foreground">Cargando…</td></tr>
                            )}
                            {!loading && tars.length === 0 && (
                                <tr><td colSpan={7} className="px-3 py-4 text-center text-muted-foreground">Sin tarifarios vigentes</td></tr>
                            )}
                            {tars.map((t) => <RowSummary key={t.id} tar={t} />)}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            <p className="text-xs text-muted-foreground">
                Para crear una nueva versión usa el endpoint
                <code className="mx-1 bg-muted px-1 py-0.5 rounded">POST /api/honorarios/tarifario</code>
                desde la terminal (próxima iteración: wizard UI). La versión vigente se cerrará automáticamente.
            </p>
        </div>
    );
}
